import React, { FC, useRef, useCallback } from 'react'
import styled from 'styled-components'

import { SettingsPopupProps } from '../../SettingsPopup'
import { useBotFlow } from '../../../../store/flow'
import { useSettingsPopupState } from '../../hooks'
import { ModalContent, ModalFooter, ModalFooterButton } from '../../styles'
import { FeedbotRjsfForm } from '../../../design-system/FeedbotRjsfForm'
import Form from '@rjsf/core'
import { feedbotToast } from '../../../../lib/feedbotToast'
// Import the bot flow schema to extract wizard settings schema
import botFlowSchema from '@feedyou-ai/feedbot-schema/schema/botFlow.json'

// Extract wizard settings schema from bot flow schema to avoid duplication
const wizardConfigSchema = (botFlowSchema as any).properties.wizardSettings

const wizardConfigUiSchema = {
  generalSettingsSchema: {
    'ui:widget': 'EditorJavascriptWidget',
    'ui:help': 'Enter JSON Schema for the general settings form'
  },

  generalSettingsUiSchema: {
    'ui:widget': 'EditorJavascriptWidget',
    'ui:help': 'Optionally enter RJSF UI Schema for the general settings form customization'
  },
  availableModules: {
    items: {
      description: { 'ui:widget': 'textarea' },
      tags: {
        'ui:help': 'Tags to categorize the module (e.g., "telephony", "routing", "faq")'
      },
      schema: {
        'ui:widget': 'EditorJavascriptWidget',
        'ui:help': 'Enter JSON Schema for module configuration'
      },
      uiSchema: {
        'ui:widget': 'EditorJavascriptWidget',
        'ui:help': 'Optionally enter RJSF UI Schema for module form customization'
      }
    }
  }
}

// Helper to serialize settings for form editing (now fields are already strings in the bot flow)
const serializeWizardSettings = (settings: any) => {
  if (!settings) {
    return {
      generalSettingsSchema: '{}',
      generalSettingsUiSchema: '{}',
      availableModules: []
    }
  }

  return {
    generalSettingsSchema: settings.generalSettingsSchema || '{}',
    generalSettingsUiSchema: settings.generalSettingsUiSchema || '{}',
    availableModules: Array.isArray(settings.availableModules)
      ? settings.availableModules.map((module: any) => ({
          id: module.id || '',
          name: module.name || '',
          description: module.description || '',
          icon: module.icon || '',
          tags: module.tags || [],
          schema: module.schema || '{}',
          uiSchema: module.uiSchema || '{}'
        }))
      : []
  }
}

// Helper to process form data for saving (validate JSON strings)
const deserializeWizardSettings = (formData: any) => {
  if (!formData) return {}

  try {
    // Validate that the JSON strings are valid
    if (formData.generalSettingsSchema) {
      JSON.parse(formData.generalSettingsSchema)
    }
    if (formData.generalSettingsUiSchema) {
      JSON.parse(formData.generalSettingsUiSchema)
    }

    formData.availableModules?.forEach((module: any) => {
      if (module.schema) {
        JSON.parse(module.schema)
      }
      if (module.uiSchema) {
        JSON.parse(module.uiSchema)
      }
    })

    // Return the form data as-is since fields are now stored as strings
    return {
      generalSettingsSchema: formData.generalSettingsSchema || '{}',
      generalSettingsUiSchema: formData.generalSettingsUiSchema || '{}',
      availableModules: (formData.availableModules || []).map((module: any) => ({
        ...module,
        tags: module.tags || [],
        schema: module.schema || '{}',
        uiSchema: module.uiSchema || '{}'
      }))
    }
  } catch (error) {
    console.error('Error validating wizard settings JSON:', error)
    throw new Error('Invalid JSON in wizard settings. Please check your input.')
  }
}

export const WizardConfigurationForm: FC<SettingsPopupProps> = props => {
  const flow = useBotFlow()
  const formRef = useRef<Form>(null)

  // Create a stable string representation for memoization
  const wizardSettingsString = React.useMemo(() => {
    return JSON.stringify({
      generalSettingsSchema: flow?.wizardSettings?.generalSettingsSchema || '{}',
      generalSettingsUiSchema: flow?.wizardSettings?.generalSettingsUiSchema || '{}',
      availableModules: flow?.wizardSettings?.availableModules || []
    })
  }, [
    flow?.wizardSettings?.generalSettingsSchema,
    flow?.wizardSettings?.generalSettingsUiSchema,
    flow?.wizardSettings?.availableModules
  ])

  // Memoize the serialized settings to prevent infinite loops
  const initialFormData = React.useMemo(
    () => serializeWizardSettings(flow?.wizardSettings),
    [wizardSettingsString]
  )

  const { formData, onFormDataChange } = useSettingsPopupState(
    () => initialFormData,
    [wizardSettingsString]
  )

  const handleFormChange = useCallback(
    (data: any) => {
      onFormDataChange(data.formData, false)
    },
    [onFormDataChange]
  )

  const handleSave = useCallback(() => {
    try {
      const deserializedSettings = deserializeWizardSettings(formData)
      props.onChange?.({ formData: { ...flow, wizardSettings: deserializedSettings } }, false)
      props.save?.({ ...flow, wizardSettings: deserializedSettings })
    } catch (error: any) {
      feedbotToast.error({
        title: 'Failed to save wizard settings',
        content: error.message || 'An error occurred while saving'
      })
    }
  }, [formData, flow, props])

  if (!flow) {
    return null
  }

  return (
    <>
      <ModalContent>
        <Description>
          Configure the wizard interface by defining the general settings schema and available
          modules. Use JSON format for schemas.
        </Description>
        <FeedbotRjsfForm
          ref={formRef}
          schema={wizardConfigSchema}
          uiSchema={wizardConfigUiSchema}
          formData={formData}
          onChange={handleFormChange}
        >
          <div /> {/* Empty div to hide default submit button */}
        </FeedbotRjsfForm>
      </ModalContent>

      <ModalFooter>
        <ModalFooterButton
          label="Close"
          className="e2e-close-bot-modal-button"
          onClick={props.close}
        />

        <ModalFooterButton
          emphasis="primary"
          label="Save"
          className="e2e-modal-success-button"
          onClick={handleSave}
        />
      </ModalFooter>
    </>
  )
}

const Description = styled.p`
  color: #666;
  margin-bottom: 24px;
  font-size: 14px;
`
